/**
 * storefront-client.js — single gateway for every Storefront API call.
 *
 * Every cartCreate discount probe on this store (PLP/search checker, PDP
 * price, header popover, card sections, cart FBT) shares ONE rate-limit
 * bucket: same public token, same buyer IP. Uncoordinated callers reading
 * that bucket in parallel is what tripped THROTTLED — so no consumer may
 * fetch graphql.json directly. They go through this client, which
 * provides:
 *
 *  - a global queue (bounded in-flight, paced dispatches) so bursts stay
 *    inside the shared bucket;
 *  - THROTTLED backoff that pauses the WHOLE queue (the bucket is shared —
 *    retrying a different request during the pause is guaranteed waste);
 *  - a unified discount cache (localStorage, 1h TTL) shared across all
 *    surfaces, plus in-flight dedup per variant;
 *  - the order-level guardrail: order-level automatic discounts (e.g.
 *    "$10 off $50+") allocate across every line of a multi-line cart and
 *    would paint fake sale prices on PLP cards. Business decision
 *    2026-06-12: order-level discounts stay active in the store but are
 *    shown only in the cart — never on PLP/search/PDP. Cart-level
 *    discountAllocations identify them, and matching line allocations are
 *    discarded before any price math.
 *
 * Diagnostics: window.__sfClientStats.
 */
(function () {
  'use strict';
  if (window.StorefrontClient) return;

  const TOKEN = (window.SF_API_TOKEN || '45f7010181888c9a9ad70475c754078b');
  const API = '/api/2025-01/graphql.json';

  // v2: the batched single-probe (pre peel-the-winner) cached FALSE NEGATIVES —
  // every product on a non-winning automatic-discount tier was stored as
  // "no discount" because Shopify applies only one automatic discount per cart.
  // Bumped so those poisoned entries are dropped on deploy instead of hiding
  // real sale prices on PLP/PDP/cards for up to the 1h TTL.
  const CACHE_KEY = 'sf_discount_cache_v2';
  const CACHE_TTL_MS = 60 * 60 * 1000; // 1h, matches the Hartville reference
  const CACHE_MAX_ENTRIES = 800;

  const MAX_LINES_PER_CART = 250; // Shopify input-array cap
  const MAX_RETRIES = 3;

  const stats = {
    requests: 0,
    cacheHits: 0,
    throttled: 0,
    errors: 0,
    orderLevelFiltered: 0,
    queuePausedMs: 0,
    peelRounds: 0,
    isolatedProbes: 0,
  };
  window.__sfClientStats = stats;

  function delay(ms) { return new Promise(r => setTimeout(r, ms)); }
  function jitter(ms) { return ms + Math.floor(Math.random() * 500); }

  // ---------------------------------------------------------------------------
  // Global request queue — every Storefront call on the page funnels through
  // here. Normal: 4 in flight, 120ms between dispatches. After a THROTTLED:
  // cautious (1 in flight, 1s pacing) for 30s. During backoff the queue is
  // fully paused.
  // ---------------------------------------------------------------------------

  const PACE_NORMAL_MS = 120;
  const PACE_CAUTIOUS_MS = 1000;
  const CAUTIOUS_WINDOW_MS = 30 * 1000;

  const queue = [];
  let inFlight = 0;
  let pausedUntil = 0;
  let cautiousUntil = 0;
  let pumpScheduled = false;

  function maxConcurrency() { return Date.now() < cautiousUntil ? 1 : 4; }
  function paceMs() { return Date.now() < cautiousUntil ? PACE_CAUTIOUS_MS : PACE_NORMAL_MS; }

  function schedulePump(afterMs) {
    if (pumpScheduled) return;
    pumpScheduled = true;
    setTimeout(() => { pumpScheduled = false; pump(); }, afterMs || 0);
  }

  function pump() {
    if (queue.length === 0) return;
    const now = Date.now();
    if (now < pausedUntil) { schedulePump(pausedUntil - now + 50); return; }
    while (inFlight < maxConcurrency() && queue.length > 0) {
      const job = queue.shift();
      inFlight++;
      executeJob(job).finally(() => {
        inFlight--;
        schedulePump(paceMs());
      });
    }
  }

  function isThrottled(json) {
    return !!(json && json.errors && json.errors.some(e => e.extensions?.code === 'THROTTLED'));
  }

  // Storefront responses normally omit extensions.cost, but read it
  // defensively: if Shopify ever exposes throttleStatus here, a draining
  // bucket flips us cautious BEFORE the hard THROTTLED hits.
  function adaptFromCost(json) {
    const ts = json?.extensions?.cost?.throttleStatus;
    if (!ts || !ts.maximumAvailable) return;
    if (ts.currentlyAvailable / ts.maximumAvailable < 0.3) {
      cautiousUntil = Date.now() + CAUTIOUS_WINDOW_MS;
    }
  }

  async function executeJob(job) {
    try {
      stats.requests++;
      const res = await fetch(API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': TOKEN,
        },
        body: JSON.stringify({ query: job.query, variables: job.variables }),
      });
      const json = await res.json();

      if (isThrottled(json)) {
        stats.throttled++;
        cautiousUntil = Date.now() + CAUTIOUS_WINDOW_MS;
        job.attempts++;
        if (job.attempts <= MAX_RETRIES) {
          // Pause the WHOLE queue, then retry this job first.
          const pause = jitter(2000 * job.attempts);
          stats.queuePausedMs += pause;
          pausedUntil = Math.max(pausedUntil, Date.now() + pause);
          queue.unshift(job);
          return;
        }
        job.resolve(null);
        return;
      }

      adaptFromCost(json);
      job.resolve(json);
    } catch {
      stats.errors++;
      job.attempts++;
      if (job.attempts <= MAX_RETRIES) {
        queue.push(job); // network hiccup — retry at the back, no global pause
        return;
      }
      job.resolve(null);
    }
  }

  // Queue a GraphQL operation. Resolves with the parsed JSON, or null once
  // retries are exhausted (throttled/network). Never rejects.
  function request(query, variables) {
    return new Promise(resolve => {
      queue.push({ query, variables, resolve, attempts: 0 });
      schedulePump();
    });
  }

  // ---------------------------------------------------------------------------
  // Unified discount cache — one localStorage key shared by every surface, so
  // a variant resolved on the PLP never re-fetches in the header popover, the
  // PDP, or the cart. Only DEFINITIVE results are stored (discount or
  // confirmed no-discount); throttled/unresolved lookups stay uncached so the
  // next render retries them.
  // ---------------------------------------------------------------------------

  function loadCache() {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return {};
      const all = JSON.parse(raw);
      const now = Date.now();
      const fresh = {};
      for (const [id, entry] of Object.entries(all)) {
        if (entry && entry.ts && now - entry.ts < CACHE_TTL_MS && entry.v) fresh[id] = entry;
      }
      return fresh;
    } catch { return {}; }
  }

  // Prune expired entries once at boot. The pruned object stays around as the
  // in-memory copy ({ v, ts } per id) so reads never re-parse localStorage;
  // persistence is debounced because a PLP settles a whole grid of entries in
  // one burst. TTL is also enforced on every read — long-lived tabs must not
  // serve stale prices forever.
  const persisted = loadCache();

  let flushTimer = null;
  function flushCache() {
    flushTimer = null;
    try {
      const ids = Object.keys(persisted);
      if (ids.length > CACHE_MAX_ENTRIES) {
        ids.sort((a, b) => persisted[a].ts - persisted[b].ts);
        for (const drop of ids.slice(0, ids.length - CACHE_MAX_ENTRIES)) delete persisted[drop];
      }
      localStorage.setItem(CACHE_KEY, JSON.stringify(persisted));
    } catch {}
  }
  flushCache();

  function cacheGet(variantId) {
    const entry = persisted[String(variantId)];
    if (!entry || !entry.ts || Date.now() - entry.ts >= CACHE_TTL_MS) return null;
    return entry.v;
  }

  function cacheSet(variantId, info) {
    persisted[String(variantId)] = { v: info, ts: Date.now() };
    if (!flushTimer) flushTimer = setTimeout(flushCache, 250);
  }

  function cacheAll() {
    const out = {};
    const now = Date.now();
    for (const [id, entry] of Object.entries(persisted)) {
      if (entry && entry.ts && now - entry.ts < CACHE_TTL_MS) out[id] = entry.v;
    }
    return out;
  }

  // ---------------------------------------------------------------------------
  // Discount probe — one batched cartCreate resolves a whole grid.
  // ---------------------------------------------------------------------------

  // lines(first: N) is interpolated with the EXACT batch size: requested cost
  // is reserved up front, so a fixed first:250 bills ~101 pts even for a
  // 24-product page (~30 pts when sized to fit).
  function buildProbeQuery(first) {
    return `
    mutation SfDiscountProbe($lines: [CartLineInput!]!) {
      cartCreate(input: { lines: $lines }) {
        cart {
          discountAllocations {
            discountedAmount { amount }
            ... on CartAutomaticDiscountAllocation { title }
            ... on CartCodeDiscountAllocation { code }
            ... on CartCustomDiscountAllocation { title }
          }
          lines(first: ${first}) {
            edges {
              node {
                merchandise { ... on ProductVariant { id } }
                cost {
                  amountPerQuantity { amount }
                  compareAtAmountPerQuantity { amount }
                  totalAmount { amount }
                }
                discountAllocations {
                  discountedAmount { amount }
                  ... on CartAutomaticDiscountAllocation { title }
                  ... on CartCodeDiscountAllocation { code }
                  ... on CartCustomDiscountAllocation { title }
                }
              }
            }
          }
        }
        userErrors { field message }
      }
    }
  `;
  }

  function extractVariantNumericId(gid) {
    const match = String(gid || '').match(/ProductVariant\/(\d+)/);
    return match ? match[1] : null;
  }

  function allocLabel(a) { return a.title || a.code || ''; }

  // Parse one probe cart into Map<numericId, info>.
  //
  // info shapes:
  //   { hasDiscount: true, discountTitle, originalPrice, finalPrice } — sale
  //   { hasDiscount: false, originalPrice, finalPrice }               — no sale
  //   { unresolved: true } — line clamped to qty 0 (variant out of stock):
  //     a qty-0 line never receives allocations, so it proves nothing; the
  //     caller re-checks via an in-stock sibling.
  function parseProbeCart(cart) {
    const results = new Map();
    const edges = cart?.lines?.edges || [];

    // Order-level guardrail: cart-level discountAllocations list exactly the
    // discounts applied to the whole order — discard their line allocations.
    const orderLevelTitles = new Set(
      (cart?.discountAllocations || []).map(allocLabel).filter(Boolean)
    );

    let filteredAny = false;
    for (const edge of edges) {
      const node = edge.node;
      const numericId = extractVariantNumericId(node.merchandise?.id);
      if (!numericId) continue;

      const rawAllocations = node.discountAllocations || [];
      const allocations = orderLevelTitles.size > 0
        ? rawAllocations.filter(a => !orderLevelTitles.has(allocLabel(a)))
        : rawAllocations;
      if (allocations.length !== rawAllocations.length) filteredAny = true;

      const perQty = parseFloat(node.cost?.amountPerQuantity?.amount || 0);
      const total = parseFloat(node.cost?.totalAmount?.amount || 0);
      const totalDiscount = allocations.reduce(
        (sum, a) => sum + parseFloat(a.discountedAmount?.amount || 0), 0
      );
      const hasDiscount = totalDiscount > 0;

      if (!hasDiscount && total === 0 && perQty > 0) {
        results.set(numericId, { unresolved: true });
        continue;
      }

      if (hasDiscount) {
        results.set(numericId, {
          hasDiscount: true,
          discountTitle: allocations.map(allocLabel).filter(Boolean).join(', '),
          originalPrice: perQty,
          finalPrice: Math.max(perQty - totalDiscount, 0),
        });
      } else {
        results.set(numericId, { hasDiscount: false, originalPrice: perQty, finalPrice: perQty });
      }
    }

    if (filteredAny) {
      stats.orderLevelFiltered++;
      if (stats.orderLevelFiltered === 1) {
        console.warn('[StorefrontClient] Order-level discount detected and excluded from PLP/PDP prices (shown in cart only):', [...orderLevelTitles].join(', '));
      }
    }
    return results;
  }

  // One probe cart for up to 250 variants. Returns Map<id, info> covering
  // every requested id, or null if the whole probe failed (callers leave
  // those ids unchecked so the next render retries — never cache a false
  // negative). One invalid variant id (e.g. unpublished to the token's
  // channel) rejects the WHOLE cart via userErrors — collect the bad ids,
  // retry without them, and settle them as no-discount.
  async function probeChunk(variantIds, badIds) {
    badIds = badIds || new Set();
    const lines = variantIds.map(id => ({
      merchandiseId: `gid://shopify/ProductVariant/${id}`,
      quantity: 1,
    }));

    const json = await request(buildProbeQuery(lines.length), { lines });
    if (json === null) return null;

    const payload = json?.data?.cartCreate;
    const userErrors = payload?.userErrors || [];
    const edges = payload?.cart?.lines?.edges || [];

    // Broken-but-not-throttled response (top-level errors with no data,
    // cart null without userErrors): treat the whole probe as undetermined.
    // Falling through would cache a false "no discount" for 1h on EVERY
    // surface at once.
    if (!payload || (!payload.cart && userErrors.length === 0)) return null;

    if (userErrors.length > 0 && edges.length === 0) {
      let foundNew = false;
      for (const err of userErrors) {
        const idx = err.field?.find(f => /^\d+$/.test(f));
        if (idx != null && lines[idx]) {
          const num = extractVariantNumericId(lines[idx].merchandiseId);
          if (num && !badIds.has(num)) { badIds.add(num); foundNew = true; }
        }
      }
      if (foundNew) {
        const cleanIds = variantIds.filter(id => !badIds.has(String(id)));
        const rest = cleanIds.length > 0 ? await probeChunk(cleanIds, badIds) : new Map();
        if (rest === null) return null;
        for (const bad of badIds) rest.set(String(bad), { hasDiscount: false });
        return rest;
      }
      return new Map(variantIds.map(id => [String(id), { hasDiscount: false }]));
    }

    const results = parseProbeCart(payload?.cart);
    for (const bad of badIds) results.set(String(bad), { hasDiscount: false });
    return results;
  }

  // In-flight dedup: two surfaces asking for the same variant share one call.
  const pending = new Map();

  /**
   * Resolve automatic-discount info for a list of variant ids.
   * Returns Map<variantId(string), info|null> — see parseProbeCart for the
   * info shapes; null means undetermined (throttled/failed): show the base
   * price and let the next render retry. Definitive results are cached for
   * 1h and shared across every surface.
   */
  function getDiscounts(variantIds) {
    const ids = [...new Set((variantIds || []).map(String).filter(Boolean))];
    const out = new Map();
    const waits = [];
    const misses = [];

    for (const id of ids) {
      const cached = cacheGet(id);
      if (cached !== null) {
        stats.cacheHits++;
        out.set(id, cached);
      } else if (pending.has(id)) {
        waits.push(pending.get(id).then(info => out.set(id, info)));
      } else {
        misses.push(id);
      }
    }

    for (let i = 0; i < misses.length; i += MAX_LINES_PER_CART) {
      const chunkIds = misses.slice(i, i + MAX_LINES_PER_CART);
      const chunkPromise = probeChunk(chunkIds);
      for (const id of chunkIds) {
        const p = chunkPromise.then(map => {
          if (map === null) return null;
          const info = map.get(id) || { hasDiscount: false };
          if (!info.unresolved) cacheSet(id, info);
          return info;
        }).finally(() => pending.delete(id));
        pending.set(id, p);
        waits.push(p.then(info => out.set(id, info)));
      }
    }

    return Promise.all(waits).then(() => out);
  }

  /**
   * Resolve automatic-discount info for a list of variant ids the SAME WAY the
   * PDP and checkout do — each variant's OWN discount, never suppressed by a
   * higher-tier discount on a neighbour in the cart.
   *
   * Why this exists: Shopify applies only ONE automatic discount per cart, and
   * these "X% off Sale Items" promos are non-combinable. A single batched probe
   * cart (getDiscounts) therefore returns a discount only for the lines on the
   * single winning tier; every product on a lower tier comes back with NO
   * discount even though it is genuinely on sale (it shows fine on the PDP,
   * whose cart holds only that product). See parseProbeCart for the shapes.
   *
   * Strategy — "parallel peel": ONE batched probe settles the winning tier's
   * lines (and, when no winner emerges, confirms the whole set has no
   * discount); every remaining "loser" is re-probed in its OWN single-line
   * cart, dispatched concurrently through the global queue. A single-line
   * cart is exactly the PDP's cart, so its own tier always wins — the result
   * is definitive by construction, no matter how many tiers are active.
   *
   * Cache correctness: a batched-loser is NOT a confirmed no-discount, so
   * round 1 never caches losers. A single-line probe IS definitive — positive
   * or negative — so its result is always cached. `unresolved` (OOS qty-0
   * line) passes through uncached in both phases so the caller's sibling path
   * can rescue it.
   *
   * Returns Map<variantId(string), info|null> covering every requested id;
   * null means undetermined (throttled/failed) — show the base price and retry.
   */
  async function getDiscountsIsolated(variantIds) {
    const ids = [...new Set((variantIds || []).map(String).filter(Boolean))];
    const out = new Map();
    const remaining = [];

    for (const id of ids) {
      const cached = cacheGet(id);
      if (cached !== null) { stats.cacheHits++; out.set(id, cached); }
      else remaining.push(id);
    }
    if (remaining.length === 0) return out;

    // Round 1 — one batched probe for the whole set.
    stats.peelRounds++;
    const map = await probeChunk(remaining);
    if (map === null) {
      for (const id of remaining) out.set(id, null);
      return out;
    }

    const losers = [];
    let foundWinner = false;
    for (const id of remaining) {
      const info = map.get(id) || { hasDiscount: false };
      if (info.unresolved) {
        // OOS representative: a qty-0 line proves nothing about its discount.
        // Hand it back uncached for the caller's sibling rescue.
        out.set(id, info);
      } else if (info.hasDiscount) {
        out.set(id, info);
        cacheSet(id, info); // definitive winner
        foundWinner = true;
      } else {
        losers.push(id); // maybe a lower-tier item suppressed this round
      }
    }

    if (!foundWinner) {
      // No winner → nothing was suppressed → the losers truly have no discount.
      for (const id of losers) {
        const info = { hasDiscount: false };
        out.set(id, info);
        cacheSet(id, info);
      }
      return out;
    }

    // Isolation phase — each loser in its own single-line cart, in parallel.
    stats.isolatedProbes += losers.length;
    await Promise.all(losers.map(async (id) => {
      const single = await probeChunk([id]);
      if (single === null) { out.set(id, null); return; }
      const info = single.get(id) || { hasDiscount: false };
      out.set(id, info);
      if (!info.unresolved) cacheSet(id, info);
    }));

    return out;
  }

  window.StorefrontClient = {
    request,
    getDiscounts,
    getDiscountsIsolated,
    cacheGet,
    cacheSet,
    cacheAll,
    stats,
  };
})();
